import { t } from '@/i18n'
import { handleApiError } from '@/utils/apiHelpers'
import { notificationService } from '@/services/notificationService'
import internalOrdersQueries from '@/api/modules/internalOrdersQueries'
import jobsQueries from '@/api/modules/jobsQueries'

import type { AxiosResponse } from 'axios'
import type {
    InternalOrderNumberConfigurationTypesResponseSchema,
    InternalOrderResponseSchema,
    InternalOrderStatusesResponseSchema,
    InternalOrdersAddCommentPayloadSchema,
    InternalOrdersAddCommentResponseSchema,
    InternalOrdersCopyProductAttachmentsPayloadSchema,
    InternalOrdersCopyProductAttachmentsResponseSchema,
    InternalOrdersCreateOrderPayloadSchema,
    InternalOrdersListResponseSchema,
    InternalOrdersUpdateOrderPayloadSchema
} from '@/api/schemas/orders/internal'

class InternalOrdersService {
    async fetchOrders(queryString = ''): Promise<AxiosResponse<InternalOrdersListResponseSchema>> {
        try {
            return await internalOrdersQueries.fetchOrders(queryString)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.fetchOrdersError'))
            throw error
        }
    }

    async fetchOrder(orderId: number | string): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        try {
            return await internalOrdersQueries.fetchOrder(orderId)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.fetchOrderError'))
            throw error
        }
    }

    async fetchOrderStatuses(): Promise<AxiosResponse<InternalOrderStatusesResponseSchema>> {
        try {
            return await internalOrdersQueries.fetchOrderStatuses()
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.fetchStatusesError'))
            throw error
        }
    }

    async fetchNumberConfigurationTypes(): Promise<AxiosResponse<InternalOrderNumberConfigurationTypesResponseSchema>> {
        try {
            return await internalOrdersQueries.fetchNumberConfigurationTypes()
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.fetchNumberConfigurationTypesError'))
            throw error
        }
    }

    async createOrder(orderData: InternalOrdersCreateOrderPayloadSchema): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        try {
            return await internalOrdersQueries.createOrder(orderData)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.createOrderError'))
            throw error
        }
    }

    async uploadDeliveryAttachment(formData: FormData): Promise<AxiosResponse<unknown>> {
        try {
            return await internalOrdersQueries.uploadDeliveryAttachment(formData)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.createDeliveryAttachmentError'))
            throw error
        }
    }

    async updateOrder(orderData: InternalOrdersUpdateOrderPayloadSchema): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        try {
            return await internalOrdersQueries.updateOrder(orderData)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.updateOrderError'))
            throw error
        }
    }

    async deleteOrder(orderId: number | string): Promise<AxiosResponse<unknown>> {
        try {
            return await internalOrdersQueries.deleteOrder(orderId)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.deleteOrderError'))
            throw error
        }
    }

    async createJob(payload: Record<string, unknown>): Promise<AxiosResponse<{ id: number }>> {
        try {
            const response = await jobsQueries.createJob(payload)
            notificationService.success(t('orders.internalOrders.service.createJobSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.createJobError'))
            throw error
        }
    }

    async copyOrderProductAttachments(
        payload: InternalOrdersCopyProductAttachmentsPayloadSchema
    ): Promise<AxiosResponse<InternalOrdersCopyProductAttachmentsResponseSchema>> {
        try {
            return await internalOrdersQueries.copyOrderProductAttachments(payload)
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.copyOrderError'))
            throw error
        }
    }

    async addComment(
        commentData: InternalOrdersAddCommentPayloadSchema
    ): Promise<AxiosResponse<InternalOrdersAddCommentResponseSchema>> {
        try {
            const response = await internalOrdersQueries.addComment(commentData)
            notificationService.success(t('orders.internalOrders.service.addCommentSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.internalOrders.service.addCommentError'))
            throw error
        }
    }
}

export const internalOrdersService = new InternalOrdersService()
